import React, { createContext, useContext, useEffect, useState } from "react";
import { api, isTransientApiError } from "../lib/api";
import { registerPush } from "../lib/push";

const AuthContext = createContext(null);

const AUTH_CACHE_KEY = "mk_auth_cache";

function readAuthCache() {
    try {
        const raw = localStorage.getItem(AUTH_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
}

function writeAuthCache(user, role) {
    try {
        if (!user || !role) {
            localStorage.removeItem(AUTH_CACHE_KEY);
            return;
        }
        localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify({ user, role }));
    } catch {
        // Storage failure must never block authentication.
    }
}


const ROLE_PATHS = {
    client: "/dashboard",
    admin: "/admin",

    partner: "/partner/dashboard",
    influencer: "/influencer",
    // Wòl ajan HT fizyone ak kliyan — menm dashboard pou tout moun
    ht_agent: "/dashboard",
    agent_ht: "/dashboard",
    investor: "/investor",
    investor_agent: "/investor-agent",
    support: "/support",
    provider: "/provider",
    fournisseur: "/provider",
};

function normalizeRole(value) {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");
}

function getRoleFromData(data, fallbackRole = "") {
    return normalizeRole(
        data?.user?.role ||
        data?.role ||
        data?.account?.role ||
        fallbackRole
    );
}

export function getDashboardPath(role) {
    const normalizedRole = normalizeRole(role);
    return ROLE_PATHS[normalizedRole] || "/dashboard";
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        if (!localStorage.getItem("mk_token")) return null;
        return readAuthCache()?.user || null;
    });
    const [role, setRole] = useState(() => {
        if (!localStorage.getItem("mk_token")) return null;
        return normalizeRole(readAuthCache()?.role || "") || null;
    });
    const [token, setToken] = useState(() => localStorage.getItem("mk_token"));
    const [loading, setLoading] = useState(true);
    const [sessionError, setSessionError] = useState("");

    const saveToken = (value) => {
        if (value) {
            localStorage.setItem("mk_token", value);
        } else {
            localStorage.removeItem("mk_token");
        }
        setToken(value || null);
    };

    const applyLoginData = (data, fallbackRole = "") => {
        if (!data?.token) {
            throw new Error("Sèvè a pa retounen token koneksyon an.");
        }

        const detectedRole = getRoleFromData(data, fallbackRole);

        if (!detectedRole) {
            throw new Error("Sistèm nan pa kapab idantifye wòl kont sa a.");
        }

        const authenticatedUser = {
            ...(data?.user || {}),
            role: detectedRole,
        };

        saveToken(data.token);
        setUser(authenticatedUser);
        setRole(detectedRole);
        setSessionError("");
        writeAuthCache(authenticatedUser, detectedRole);
        registerPush({ requestPermission: false });

        return {
            ...data,
            user: authenticatedUser,
            role: detectedRole,
            redirectTo: getDashboardPath(detectedRole),
        };
    };

    const refresh = async () => {
        const savedToken = localStorage.getItem("mk_token");

        if (!savedToken) {
            writeAuthCache(null, null);
            setUser(null);
            setRole(null);
            setToken(null);
            setSessionError("");
            setLoading(false);
            return;
        }

        setToken(savedToken);
        setSessionError("");

        try {
            const { data } = await api.get("/auth/me", { timeout: 12000 });
            const detectedRole = normalizeRole(data?.role || data?.user?.role);
            const refreshedUser = {
                ...(data?.user || data || {}),
                role: detectedRole,
            };

            setUser(refreshedUser);
            setRole(detectedRole);
            writeAuthCache(refreshedUser, detectedRole);
            registerPush({ requestPermission: false });
        } catch (error) {
            const status = Number(error?.response?.status || 0);

            // Only a real authentication rejection invalidates the session.
            // Network timeouts and temporary 5xx responses must not log users out.
            if (status === 401 || status === 403) {
                saveToken(null);
                writeAuthCache(null, null);
                setUser(null);
                setRole(null);
                setSessionError("");
            } else {
                const cached = readAuthCache();
                if (cached?.user && cached?.role) {
                    const cachedRole = normalizeRole(cached.role);
                    setUser({ ...cached.user, role: cachedRole });
                    setRole(cachedRole);
                }
                setSessionError(
                    isTransientApiError(error)
                        ? "Koneksyon an pran reta. Sesyon ou rete an sekirite; eseye ankò."
                        : "Nou pa rive verifye sesyon an kounye a. Eseye ankò."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const login = async (identifier, password) => {
        const cleanIdentifier = String(identifier || "").trim();

        if (!cleanIdentifier || !password) {
            throw new Error("Tanpri antre idantifyan ou ak modpas ou.");
        }

        const attempts = [
            {
                endpoint: "/auth/login",
                payload: { identifier: cleanIdentifier, password },
                fallbackRole: "client",
            },
            {
                endpoint: "/auth/admin/login",
                payload: { username: cleanIdentifier, password },
                fallbackRole: "admin",
            },
            {
                endpoint: "/auth/partner/login",
                payload: {
                identifier: cleanIdentifier,
                password,
                },
                fallbackRole: "partner",
            },
            {
                endpoint: "/auth/investor/login",
                payload: { email: cleanIdentifier, password },
                fallbackRole: "investor",
            },
            {
                endpoint: "/auth/investor-agent/login",
                payload: { code: cleanIdentifier, password },
                fallbackRole: "investor_agent",
            },
            {
                endpoint: "/auth/influencer/login",
                payload: { identifier: cleanIdentifier, password },
                fallbackRole: "influencer",
            },
        ];

        let lastError = null;

        for (const attempt of attempts) {
            try {
                const { data } = await api.post(attempt.endpoint, attempt.payload);
                const result = applyLoginData(data, attempt.fallbackRole);
                if (result.role === "client") {
                    const attributionToken = localStorage.getItem("mk_influencer_attribution");
                    if (attributionToken) {
                        try {
                            await api.post("/influencer/attribution/claim", { token: attributionToken });
                            localStorage.removeItem("mk_influencer_attribution");
                        } catch (claimError) {
                            console.warn("Influencer attribution could not be claimed:", claimError);
                        }
                    }
                }
                return result;
            } catch (error) {
                const serverMessage =
                    error?.response?.data?.message ||
                    error?.response?.data?.detail ||
                    error?.response?.data?.error ||
                    "";

                if (
                    typeof serverMessage === "string" &&
                    serverMessage.startsWith("VERIFICATION_REQUIRED:")
                ) {
                    throw error;
                }

                lastError = error;

                // Do not hammer every role endpoint when the real problem is
                // connectivity/server availability. Only continue role probing
                // for normal credential/not-found responses.
                if (isTransientApiError(error)) {
                    throw error;
                }
            }
        }

        throw lastError || new Error("Idantifyan oswa modpas la pa kòrèk.");
    };

    const loginPasskey = async (identifier, credentialFactory) => {
        const cleanIdentifier = String(identifier || "").trim();
        if (!cleanIdentifier) throw new Error("Antre imèl, telefòn oswa ID MKTRANSFE ou an premye.");
        const { data: begin } = await api.post("/auth/passkey/begin", { identifier: cleanIdentifier });
        const credential = await credentialFactory(begin.publicKey);
        const { data } = await api.post("/auth/passkey/finish", { challengeId: begin.challengeId, credential });
        return applyLoginData(data, "client");
    };

    const loginClient = async (identifier, password) => {
        const { data } = await api.post("/auth/login", { identifier, password });
        return applyLoginData(data, "client");
    };

    const loginAdmin = async (username, password) => {
        const { data } = await api.post("/auth/admin/login", { username, password });
        return applyLoginData(data, "admin");
    };

    const loginPartner = async (identifier, password) => {
    const { data } = await api.post(
        "/auth/partner/login",
        {
            identifier,
            password,
        }
    );

    return applyLoginData(
        data,
        "partner"
    );
};

    const loginInvestor = async (email, password) => {
        const { data } = await api.post("/auth/investor/login", { email, password });
        return applyLoginData(data, "investor");
    };

    const loginInvestorAgent = async (code, password) => {
        const { data } = await api.post("/auth/investor-agent/login", { code, password });
        return applyLoginData(data, "investor_agent");
    };

    const register = async (firstName, lastName, contact, email, password, country, promoCode = "", legal = {}) => {
        const { data } = await api.post("/auth/register", {
            ...legal,
            firstName,
            lastName,
            contact,
            email,
            promoCode,
            password,
            country,
        });

        const result = applyLoginData(data, "client");
        const attributionToken = localStorage.getItem("mk_influencer_attribution");
        if (attributionToken) {
            try {
                await api.post("/influencer/attribution/claim", { token: attributionToken });
                localStorage.removeItem("mk_influencer_attribution");
            } catch (error) {
                console.warn("Influencer attribution could not be claimed:", error);
            }
        }
        return result;
    };

    const claimInfluencerAttribution = async () => {
        const attributionToken = localStorage.getItem("mk_influencer_attribution");
        if (!attributionToken) return;
        try {
            await api.post("/influencer/attribution/claim", { token: attributionToken });
            localStorage.removeItem("mk_influencer_attribution");
        } catch (error) {
            console.warn("Influencer attribution could not be claimed:", error);
        }
    };

    const googleLogin = async (credential) => {
        const { data } = await api.post("/auth/google", { credential });
        if (data?.profileRequired) return data;

        const result = applyLoginData(data, "client");
        await claimInfluencerAttribution();
        return result;
    };

    const googleCompleteRegistration = async (signupToken, contact, country, promoCode = "", legal = {}) => {
        const { data } = await api.post("/auth/google/complete", {
            ...legal,
            signupToken,
            contact,
            country,
            promoCode,
        });
        const result = applyLoginData(data, "client");
        await claimInfluencerAttribution();
        return result;
    };

    const logout = () => {
        saveToken(null);
        writeAuthCache(null, null);
        setUser(null);
        setRole(null);
        setSessionError("");
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                role,
                token,
                loading,
                sessionError,
                login,
                loginPasskey,
                loginClient,
                loginAdmin,
                loginPartner,
                loginInvestor,
                loginInvestorAgent,
                register,
                googleLogin,
                googleCompleteRegistration,
                logout,
                refresh,
                setUser,
                getDashboardPath,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);

